import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { translate } from '../Services/translation';
const DEFAULT_DISPLAY_MS = 3500;
let globalId = 0;
export default function CartAddToastRoot({ maxVisible = 2, displayMs = DEFAULT_DISPLAY_MS, mergeDuplicates = false }) {
    const [toasts, setToasts] = useState([]);
    const timeoutsRef = useRef(new Map());
    const mountedRef = useRef(true);
    useEffect(() => () => {
        mountedRef.current = false;
        // Clear pending timeouts to avoid setState calls after unmount
        timeoutsRef.current.forEach(clearTimeout);
    }, []);
    const removeToast = useCallback((id) => {
        if (!mountedRef.current)
            return;
        setToasts(curr => curr.filter(t => t.id !== id));
        timeoutsRef.current.delete(id);
    }, []);
    const onAdded = useCallback((e) => {
        const { articleNumber, quantity, name } = e.detail || {};
        const finalQty = quantity || 1;
        const label = `${finalQty} × ${name || articleNumber}`;
        setToasts(curr => {
            const existing = curr.find(t => t.label === label);
            if (mergeDuplicates && existing) {
                const updated = curr.map(t => t.id === existing.id ? { ...t, count: (t.count || 1) + 1 } : t);
                // Refresh timeout for existing toast
                const oldTimeout = timeoutsRef.current.get(existing.id);
                if (oldTimeout)
                    clearTimeout(oldTimeout);
                const newTimeout = setTimeout(() => removeToast(existing.id), displayMs);
                timeoutsRef.current.set(existing.id, newTimeout);
                return updated;
            }
            // Create new toast
            const id = ++globalId;
            const newEntry = { id, label, count: mergeDuplicates ? 1 : undefined };
            const timeoutId = setTimeout(() => removeToast(id), displayMs);
            timeoutsRef.current.set(id, timeoutId);
            return [...curr, newEntry];
        });
    }, [mergeDuplicates, displayMs, removeToast]);
    useEffect(() => {
        window.addEventListener('cart:itemAdded', onAdded);
        return () => window.removeEventListener('cart:itemAdded', onAdded);
    }, [onAdded]);
    if (!toasts.length)
        return null;
    return createPortal(_jsx("div", { className: "cart-add-toast-stack", role: "status", "aria-live": "polite", "aria-atomic": "false", children: toasts.slice(-maxVisible).map(t => (_jsxs("div", { className: "cart-add-toast", children: [_jsx("span", { className: "cart-add-toast__icon", "aria-hidden": "true", children: "\u2713" }), _jsxs("span", { className: "cart-add-toast__text", children: [translate('nmas.cart.toast.added'), ": ", t.label, t.count && t.count > 1 ? ` (x${t.count})` : ''] })] }, t.id))) }), document.body);
}
